import axios from "axios";
import {
  DEMO_BUSINESS_UNIT_ID,
  isDemoMode,
  markDemoDocument,
} from "./demoMode";
import { isMutatingHttpMethod } from "./demoAccessPolicy";
import { loadDemoSnapshot, validateDemoReservationSettings, validateDemoFrontDeskSettings, validateDemoExtrasSettings, validateDemoChargesSettings, validateDemoBookingEngineSettings } from "./demoSnapshot";

const NETWORK_AUDIT_KEY = "__PILCORE_DEMO_NETWORK_AUDIT__";

function clone(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

function audit(kind, request) {
  if (typeof window === "undefined") return;
  const current = window[NETWORK_AUDIT_KEY] || { handled: [], blocked: [] };
  current[kind].push({ ...request, at: new Date().toISOString() });
  if (current[kind].length > 100) current[kind].shift();
  window[NETWORK_AUDIT_KEY] = current;
  document.documentElement.dataset.pilcoreDemoHandledRequests = String(current.handled.length);
  document.documentElement.dataset.pilcoreDemoBlockedRequests = String(current.blocked.length);
}

function buildRequestUrl(config = {}) {
  const rawUrl = String(config.url || "/");
  const url = new URL(rawUrl, window.location.origin);
  const params = config.params;
  if (params && typeof params === "object") {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((entry) => url.searchParams.append(key, String(entry)));
        return;
      }
      url.searchParams.set(key, String(value));
    });
  }
  return url;
}

function guestName(reservation) {
  const row = Array.isArray(reservation?.guests) ? reservation.guests[0] : null;
  const customer = row?.customer || row || reservation?.booker || {};
  return `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim();
}

function reservationSearchText(reservation) {
  return [
    reservation?.locator,
    reservation?.group_locator,
    reservation?.external_partner_locator,
    reservation?.external_agency_name,
    reservation?.status,
    reservation?.room_category?.name,
    reservation?.room?.room_number,
    reservation?.rate?.name,
    guestName(reservation),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function reservationUpdatedAt(reservation) {
  // The export uses PostgreSQL text (space separator, short UTC offset).
  // Normalize it to ISO so browsers interpret the same instant consistently.
  const value = String(reservation?.updatedAt ?? reservation?.updated_at ?? "").trim()
    .replace(" ", "T")
    .replace(/([+-]\d{2})$/, "$1:00")
    .replace(/([+-]\d{2})(\d{2})$/, "$1:$2")
    .replace(/\.(\d+)(?=Z|[+-])/, (_match, fraction) => `.${fraction.padEnd(3, "0").slice(0, 3)}`);
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
}

function filterReservations(reservations, url) {
  let rows = [...reservations];
  const showCancelled = url.searchParams.get("show_cancelled") === "1";
  if (!showCancelled) {
    rows = rows.filter((reservation) => {
      const status = String(reservation?.status || "").toLowerCase();
      return status !== "cancelled" && status !== "canceled";
    });
  }

  const search = String(url.searchParams.get("q") || "").trim().toLowerCase();
  if (search) {
    rows = rows.filter((reservation) => reservationSearchText(reservation).includes(search));
  }

  ["check_in_date", "check_out_date"].forEach((field) => {
    const from = url.searchParams.get(`${field}_from`);
    const to = url.searchParams.get(`${field}_to`);
    if (from) rows = rows.filter((reservation) => String(reservation?.[field] || "") >= from);
    if (to) rows = rows.filter((reservation) => String(reservation?.[field] || "") <= to);
  });

  let filters = {};
  try {
    const parsed = JSON.parse(url.searchParams.get("filters") || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) filters = parsed;
  } catch (_error) {
    // Invalid optional UI filters do not affect the underlying snapshot.
  }
  rows = rows.filter((row) => Object.entries(filters).every(([key, selected]) => {
    if (!RESERVATION_FILTER_VALUES[key]) return true;
    const value = RESERVATION_FILTER_VALUES[key](row);
    if (Array.isArray(selected)) {
      return !selected.length || selected.some((entry) => String(entry).toLowerCase() === String(value).toLowerCase());
    }
    return !String(selected || "").trim() || String(value).toLowerCase().includes(String(selected).trim().toLowerCase());
  }));

  // Match the PMS list's updatedAt DESC order before pagination.
  return rows.sort((left, right) => {
    const leftUpdatedAt = reservationUpdatedAt(left);
    const rightUpdatedAt = reservationUpdatedAt(right);
    if (leftUpdatedAt !== rightUpdatedAt) return leftUpdatedAt > rightUpdatedAt ? -1 : 1;
    // Stable pages when updates have the same timestamp or no valid timestamp.
    return Number(right?.id || 0) - Number(left?.id || 0);
  });
}

const RESERVATION_FILTER_VALUES = {
  locator: (row) => row.locator || row.group_locator || "",
  externalLocator: (row) => row.external_partner_locator || "",
  customer: guestName,
  roomCategory: (row) => row.room_category?.name || row.effective_room_category?.name || "—",
  room: (row) => row.effective_room?.room_number || row.room?.room_number || "—",
  rate: (row) => row.rate?.name || "—",
  checkIn: (row) => row.check_in_date || "—",
  checkOut: (row) => row.check_out_date || "—",
  roomNights: (row) => Number(row.room_nights || 0),
  adults: (row) => Number(row.adults || 0),
  children: (row) => Number(row.children || 0),
  babies: (row) => Number(row.babies || 0),
  status: (row) => row.status || "—",
  totalPrice: (row) => Number(row.total_price || 0) + Number(row.reservation_extras_total || 0),
  externalAgency: (row) => row.external_agency_name || "—",
  createdAt: (row) => row.createdAt || row.created_at || "",
};

function buildFilterOptions(state) {
  const optionKeys = ["roomCategory", "room", "rate", "checkIn", "checkOut", "roomNights", "adults", "children", "babies", "status", "externalAgency"];
  return Object.fromEntries(optionKeys.map((key) => {
    const values = new Map();
    state.reservations.forEach((row) => {
      const label = RESERVATION_FILTER_VALUES[key](row);
      const value = typeof label === "number" ? label : String(label).toLowerCase();
      const option = values.get(value) || { value, label: String(label), count: 0 };
      option.count += 1;
      values.set(value, option);
    });
    return [key, [...values.values()].sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }))];
  }));
}

function ok(data, status = 200) {
  return { handled: true, status, data: clone(data) };
}

function unhandled() {
  return { handled: false, status: 501, data: null };
}

function readOnlyBlocked(method, path) {
  const requestLabel = `${String(method || "get").toUpperCase()} ${path || "/"}`;
  return {
    handled: false,
    status: 405,
    data: {
      error: `The Pilcore demo is read-only. ${requestLabel} was blocked.`,
      code: "PILCORE_DEMO_READ_ONLY",
      demo_mode: true,
      read_only: true,
    },
  };
}

function reservationDetails(row) {
  return {
    ...row,
    room_category_name: row.room_category?.name ?? null,
    rate_name: row.rate?.name ?? row.group_rate_name ?? null,
    room_number: row.effective_room_number ?? row.room?.room_number ?? null,
    total_extras: row.reservation_extras_total ?? row.extras_total,
    // The list may show a booker fallback. Detail must show actual guest links.
    guests: Array.isArray(row.check_in_readiness?.guests)
      ? row.check_in_readiness.guests : row.guests || [],
  };
}

function reservationNotFound() {
  return ok({
    error: "Reservation not included in this demo snapshot.",
    code: "PILCORE_DEMO_RESERVATION_NOT_FOUND",
  }, 404);
}

function rateNotFound() {
  return ok({ error: "Room rate not included in this demo snapshot.", code: "PILCORE_DEMO_RATE_NOT_FOUND" }, 404);
}

function rateSnapshotUpdateRequired() {
  return ok({
    error: "Actualiza el exportador y el JSON de la demo para consultar los detalles y precios de las tarifas.",
    code: "PILCORE_DEMO_SNAPSHOT_UPDATE_REQUIRED",
  }, 412);
}

function matchesDemoBusinessUnit(url) {
  const requested = url.searchParams.get("business_unit_id");
  return !requested || requested === String(DEMO_BUSINESS_UNIT_ID);
}

// Match the PMS calendar-year history cutoff, frozen to the hotel's snapshot.
// UTC calendar arithmetic avoids changing the day across visitor timezones/DST.
function rateHistoryCutoff(referenceDate) {
  const cutoff = new Date(`${referenceDate}T00:00:00Z`);
  cutoff.setUTCFullYear(cutoff.getUTCFullYear() - 1);
  return cutoff.toISOString().slice(0, 10);
}

function matrixDate(value) {
  const date = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date ? parsed : null;
}

function addMatrixDays(date, days) {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function buildRateMatrix(state, url) {
  if (!url.searchParams.get("business_unit_id")) return ok({ error: "Missing business_unit_id" }, 400);
  const today = matrixDate(state.reference_date);
  const rawRange = parseInt(url.searchParams.get("range_days") || "45", 10);
  const requestedRange = Number.isFinite(rawRange) && rawRange > 0 ? Math.min(rawRange, 180) : 45;
  const start = matrixDate(url.searchParams.get("start_date")) || today;
  const end = matrixDate(url.searchParams.get("end_date")) || addMatrixDays(start, requestedRange - 1);
  if (start < today) return ok({ error: "start_date cannot be before today" }, 400);
  if (end < start) return ok({ error: "end_date cannot be before start_date" }, 400);
  const rangeDays = Math.floor((end - start) / 86400000) + 1;
  if (rangeDays > 180) return ok({ error: "Date range cannot exceed 180 days" }, 400);

  // The matrix uses canonical price days and the stored formula flag, unlike
  // the range-based individual chart and the computed summary formula links.
  // Stop metadata is required here so a legacy JSON cannot label automatic
  // stops as manual when the screen loads both read-only endpoints together.
  if (!Array.isArray(state.roomRateDetails) || !Array.isArray(state.ratePrices) || !Array.isArray(state.stopSells)
    || state.roomRateDetails.some((row) => typeof row.matrix_has_formula_link !== "boolean")
    || state.ratePrices.some((row) => !Object.prototype.hasOwnProperty.call(row, "stay_date") || !matrixDate(row.stay_date))
    || state.stopSells.some((row) => !Object.prototype.hasOwnProperty.call(row, "is_automatic")
      || (row.is_automatic !== null && typeof row.is_automatic !== "boolean"))) {
    return ok({
      error: "Actualiza el exportador y el JSON de la demo para mostrar Room Rates Matrix.",
      code: "PILCORE_DEMO_SNAPSHOT_UPDATE_REQUIRED",
    }, 412);
  }

  const dates = Array.from({ length: rangeDays }, (_, index) => addMatrixDays(start, index).toISOString().slice(0, 10));
  if (!matchesDemoBusinessUnit(url)) return ok({ dates, matrix: [] });
  const startStr = dates[0];
  const endStr = dates[dates.length - 1];
  const pricesByRate = new Map();
  for (const row of state.ratePrices.filter((price) => price.stay_date >= startStr && price.stay_date <= endStr)
    .sort((left, right) => left.stay_date.localeCompare(right.stay_date))) {
    const prices = pricesByRate.get(row.room_rate_id) || [];
    prices.push(row);
    pricesByRate.set(row.room_rate_id, prices);
  }
  const rates = state.roomRateDetails.filter((row) => row.status === "active"
    // PostgreSQL compares these timestamp columns to UTC midnight date bounds.
    && Date.parse(row.valid_from) <= end.getTime()
    && Date.parse(row.valid_to ?? "2099-12-31T00:00:00Z") >= start.getTime())
    .sort((left, right) => left.room_category_id - right.room_category_id || left.id - right.id);
  const matrix = rates.map((rate) => {
    const matches = pricesByRate.get(rate.id) || [];
    const row = {
      room_category: rate.room_category.name,
      room_rate: `${rate.name} (${rate.rate_code})`,
      client: rate.client?.name || "–",
      room_rate_id: rate.id,
      base_rate_id: rate.base_rate_id,
      has_formula_link: rate.matrix_has_formula_link,
      min_price_amount: rate.min_price_amount ?? null,
      max_price_amount: rate.max_price_amount ?? null,
      min_stay: matches[0]?.min_stay ?? null,
      max_stay: matches[0]?.max_stay ?? null,
      room_category_id: rate.room_category_id,
      client_id: rate.client_id || null,
      prices: {},
      constraints: {},
    };
    for (const price of matches) {
      row.prices[price.stay_date] = parseFloat(price.price);
      row.constraints[price.stay_date] = { min_stay: price.min_stay ?? null, max_stay: price.max_stay ?? null };
    }
    return row;
  });
  return ok({ dates, matrix });
}

export async function handleDemoRequest(config = {}) {
  const method = String(config.method || "get").toLowerCase();
  const url = buildRequestUrl(config);
  const path = url.pathname.replace(/\/+$/, "") || "/";

  if (isMutatingHttpMethod(method)) {
    return readOnlyBlocked(method, path);
  }

  const state = await loadDemoSnapshot();

  if (method === "get" && path === "/api/business-units") {
    return ok([state.businessUnit]);
  }

  if (method === "get" && path === `/api/business-units/${DEMO_BUSINESS_UNIT_ID}`) {
    return ok(state.businessUnit);
  }

  if (method === "get" && path === "/api/currency-settings") {
    return ok({
      admin_enabled: false,
      enabled: false,
      base_currency_code: state.businessUnit.currency,
      currencies: [state.businessUnit.currency],
      rates: { [state.businessUnit.currency]: 1 },
      current_rate_set_id: null,
    });
  }

  if (method === "get" && path === "/api/reservations") {
    const filteredRows = filterReservations(state.reservations, url);
    const page = Math.max(1, Math.floor(Number(url.searchParams.get("page"))) || 1);
    const pageSize = Math.min(1000, Math.max(1, Math.floor(Number(url.searchParams.get("page_size"))) || 100));
    const start = (page - 1) * pageSize;
    return ok({
      rows: filteredRows.slice(start, start + pageSize),
      total: filteredRows.length,
      page,
      page_size: pageSize,
      total_pages: Math.max(1, Math.ceil(filteredRows.length / pageSize)),
      filter_options: buildFilterOptions(state),
    });
  }

  if (method === "get" && path === "/api/rooms") return ok(state.rooms);
  if (method === "get" && path === "/api/room_categories") return ok(state.roomCategories);
  if (method === "get" && path === "/api/admin/forecast-occupancy") {
    if (!Array.isArray(state.forecastOccupancy)) {
      return ok({
        error: "Actualiza el exportador y el JSON de la demo para mostrar Availability Summary.",
        code: "PILCORE_DEMO_SNAPSHOT_UPDATE_REQUIRED",
      }, 412);
    }
    // Preserve production forecast rows: rates and stay state may make them
    // different from the reservation calendar. Never fabricate this series.
    return ok(state.forecastOccupancy);
  }
  if (method === "get" && path === "/api/client-category-allocation/clients") return ok(state.clients);
  if (method === "get" && path === "/api/city-events") return ok(state.cityEvents);
  if (method === "get" && path === "/api/room-blocks") {
    const roomsById = new Map((state.rooms || []).map((room) => [Number(room.id), room]));
    const categoriesById = new Map((state.roomCategories || []).map((category) => [Number(category.id), category]));
    return ok((state.roomBlocks || []).map((block) => {
      const room = block.room_id == null ? null : roomsById.get(Number(block.room_id)) || null;
      // Category-only blocks need the same category association as the real PMS.
      // Resolve both associations exclusively from the exported hotel catalog.
      const categoryId = block.room_category_id ?? room?.room_category_id;
      return { ...block, room, RoomCategory: categoriesById.get(Number(categoryId)) || null };
    }));
  }
  if (method === "get" && path === "/api/room-rates") return ok(state.roomRates);
  if (method === "get" && path === "/api/roomRates/summary") {
    if (!Array.isArray(state.roomRateSummaries)) {
      return ok({
        error: "Actualiza el exportador y el JSON de la demo para mostrar Room Rates Management.",
        code: "PILCORE_DEMO_SNAPSHOT_UPDATE_REQUIRED",
      }, 412);
    }
    return ok(state.roomRateSummaries);
  }
  if (method === "get" && path === "/api/roomRates/all") {
    if (!Array.isArray(state.roomRateDetails)) return rateSnapshotUpdateRequired();
    const categoryId = url.searchParams.get("room_category_id");
    return ok(state.roomRateDetails.filter((row) => matchesDemoBusinessUnit(url) && row.status === "active"
      && (!categoryId || String(row.room_category_id) === categoryId)).sort((left, right) => left.id - right.id));
  }
  const rateDetailMatch = path.match(/^\/api\/roomRates\/(-?\d+(?:\.\d+)?)$/);
  if (method === "get" && rateDetailMatch) {
    const id = Number(rateDetailMatch[1]);
    if (!Number.isSafeInteger(id) || id <= 0 || !state.roomRates.some((row) => row.id === id)) return rateNotFound();
    if (!Array.isArray(state.roomRateDetails)) return rateSnapshotUpdateRequired();
    const rate = state.roomRateDetails.find((row) => row.id === id);
    return rate ? ok(rate) : rateNotFound();
  }
  const rateHistoryMatch = path.match(/^\/api\/rate-prices\/history\/(-?\d+(?:\.\d+)?)$/);
  if (method === "get" && rateHistoryMatch) {
    const id = Number(rateHistoryMatch[1]);
    if (!Number.isSafeInteger(id) || id <= 0 || !state.roomRates.some((row) => row.id === id)) return rateNotFound();
    if (!Array.isArray(state.ratePrices)) return rateSnapshotUpdateRequired();
    const cutoff = rateHistoryCutoff(state.reference_date);
    // Keep every qualifying stored rule, including overlapping rules and future
    // dates. Stable start-date ordering preserves the exported tie precedence.
    return ok(state.ratePrices.filter((row) => row.room_rate_id === id && row.start_date >= cutoff)
      .sort((left, right) => left.start_date.localeCompare(right.start_date)));
  }
  if (method === "get" && path === "/api/stop-sells") {
    if (!Array.isArray(state.stopSells)) return rateSnapshotUpdateRequired();
    return ok(matchesDemoBusinessUnit(url)
      ? [...state.stopSells].sort((left, right) => left.stay_date.localeCompare(right.stay_date)) : []);
  }
  if (method === "get" && path === "/api/rate-prices/matrix") return buildRateMatrix(state, url);

  if (method === "get" && path === "/api/demo/reservation-settings") {
    if (!matchesDemoBusinessUnit(url)) return ok({ error: "Hotel not included in this demo snapshot." }, 404);
    try {
      validateDemoReservationSettings(state.reservationSettings, state.clients);
    } catch (_error) {
      return ok({
        error: "Actualiza el exportador y el JSON de la demo para mostrar Reservation Settings.",
        code: "PILCORE_DEMO_SNAPSHOT_UPDATE_REQUIRED",
      }, 412);
    }
    // Only the five reservation settings sections and their scoped choice
    // labels are exposed. This never opens the global clients/settings APIs.
    return ok({
      settings: state.reservationSettings,
      clients: state.clients.map(({ id, name, status }) => ({ id, name, status })),
    });
  }
  if (method === "get" && path === "/api/demo/front-desk-settings") {
    if (!matchesDemoBusinessUnit(url)) return ok({ error: "Hotel not included in this demo snapshot." }, 404);
    try {
      return ok({ settings: validateDemoFrontDeskSettings(state.hotelSettings) });
    } catch (_error) {
      return ok({
        error: "Actualiza el exportador y el JSON de la demo para mostrar Front Desk Settings.",
        code: "PILCORE_DEMO_SNAPSHOT_UPDATE_REQUIRED",
      }, 412);
    }
  }
  if (method === "get" && path === "/api/demo/extras-settings") {
    if (!matchesDemoBusinessUnit(url)) return ok({ error: "Hotel not included in this demo snapshot." }, 404);
    try {
      return ok(validateDemoExtrasSettings(state));
    } catch (_error) {
      return ok({
        error: "Actualiza el exportador y el JSON de la demo para mostrar Extras & Discounts.",
        code: "PILCORE_DEMO_SNAPSHOT_UPDATE_REQUIRED",
      }, 412);
    }
  }
  if (method === "get" && path === "/api/demo/charges-settings") {
    if (!matchesDemoBusinessUnit(url)) return ok({ error: "Hotel not included in this demo snapshot." }, 404);
    try {
      return ok(validateDemoChargesSettings(state));
    } catch (_error) {
      return ok({
        error: "Actualiza el exportador y el JSON de la demo para mostrar Reservation Charges.",
        code: "PILCORE_DEMO_SNAPSHOT_UPDATE_REQUIRED",
      }, 412);
    }
  }
  if (method === "get" && path === "/api/demo/booking-engine-settings") {
    if (!matchesDemoBusinessUnit(url)) return ok({ error: "Hotel not included in this demo snapshot." }, 404);
    try {
      return ok(validateDemoBookingEngineSettings(state));
    } catch (_error) {
      return ok({
        error: "Actualiza el exportador y el JSON de la demo para mostrar Booking Engine Settings.",
        code: "PILCORE_DEMO_SNAPSHOT_UPDATE_REQUIRED",
      }, 412);
    }
  }

  if (method === "get" && path === "/api/hotel-settings") {
    return ok({
      ...state.hotelSettings,
      business_unit_id: DEMO_BUSINESS_UNIT_ID,
      hotel_name: state.businessUnit.name,
      base_currency: state.businessUnit.currency,
    });
  }

  const groupFullMatch = path.match(/^\/api\/reservation-groups\/(\d+)\/full$/);
  if (method === "get" && groupFullMatch) {
    const reservations = state.reservations.filter(
      (candidate) => Number(candidate?.reservation_group_id) === Number(groupFullMatch[1])
    );
    const reservation = reservations[0];
    if (!reservation) return ok({
      error: "Reservation group not included in this demo snapshot.",
      code: "PILCORE_DEMO_RESERVATION_GROUP_NOT_FOUND",
    }, 404);
    return ok({
      group: {
        id: reservation.reservation_group_id,
        locator: reservation.group_locator,
        group_locator: reservation.group_locator,
        business_unit_id: DEMO_BUSINESS_UNIT_ID,
        number_of_rooms: reservations.length,
        booker_id: reservation.booker?.id ?? null,
        booker: reservation.booker,
      },
      reservations: reservations.map(reservationDetails),
    });
  }

  const nightlyPricesMatch = path.match(/^\/api\/reservations\/(\d+)\/nightly-prices$/);
  if (method === "get" && nightlyPricesMatch) {
    const reservation = state.reservations.find((row) => Number(row.id) === Number(nightlyPricesMatch[1]));
    if (!reservation) return reservationNotFound();
    return ok(reservation.nightly_prices || []);
  }

  const reservationMatch = path.match(/^\/api\/reservations\/(\d+)$/);
  if (method === "get" && reservationMatch) {
    const reservation = state.reservations.find((row) => Number(row.id) === Number(reservationMatch[1]));
    if (!reservation) return reservationNotFound();
    return ok(reservationDetails(reservation));
  }

  if (method === "get" && path === "/api/rooms/available") {
    return ok(state.rooms);
  }

  return unhandled();
}

function blockedError(config, result) {
  const fallbackMessage = `Demo mode blocked an unhandled request: ${String(config.method || "get").toUpperCase()} ${config.url}`;
  const responseData = result?.data || {
    error: fallbackMessage,
    code: "PILCORE_DEMO_REQUEST_BLOCKED",
    demo_mode: true,
  };
  const error = new Error(responseData.error || fallbackMessage);
  error.code = responseData.code || "PILCORE_DEMO_REQUEST_BLOCKED";
  error.config = config;
  error.response = {
    status: result?.status || 501,
    statusText: result?.status === 404 ? "Not Found"
      : result?.status === 405 ? "Pilcore demo is read-only" : "Blocked by Pilcore demo mode",
    data: responseData,
    headers: { "content-type": "application/json" },
    config,
  };
  return error;
}

export async function demoAxiosAdapter(config) {
  if (config?.signal?.aborted) throw new axios.CanceledError("canceled", config);
  const result = await handleDemoRequest(config);
  const request = { method: String(config.method || "get").toUpperCase(), url: String(config.url || "") };
  if (!result.handled) {
    audit("blocked", request);
    console.warn("[Pilcore Demo] Blocked request", request);
    throw blockedError(config, result);
  }
  audit("handled", { ...request, status: result.status });
  if (result.status >= 400) throw blockedError(config, result);
  return {
    data: result.data,
    status: result.status,
    statusText: result.status >= 400 ? "Demo error" : "OK",
    headers: { "content-type": "application/json", "x-pilcore-demo": "1" },
    config,
    request: { demo: true },
  };
}

export function applyDemoAxiosAdapter(config) {
  if (!isDemoMode()) return config;
  markDemoDocument();
  document.documentElement.dataset.pilcoreDemoNetwork = "mocked";
  return { ...config, adapter: demoAxiosAdapter };
}

export function installDemoFetchGuard() {
  if (typeof window === "undefined" || window.fetch?.__pilcoreDemoGuard) return;
  const originalFetch = window.fetch.bind(window);

  const guardedFetch = async (input, init = {}) => {
    if (!isDemoMode()) return originalFetch(input, init);
    const rawUrl = typeof input === "string" ? input : input?.url;
    const url = new URL(rawUrl || "/", window.location.origin);
    const method = String(init?.method || input?.method || "GET").toLowerCase();
    const isApiRequest = url.pathname.startsWith("/api/");
    const isExternalRequest = url.origin !== window.location.origin;
    const isMutation = isMutatingHttpMethod(method);
    if (!isApiRequest && !isExternalRequest && !isMutation) return originalFetch(input, init);

    const result = isApiRequest
      ? await handleDemoRequest({ method, url: url.toString(), body: init?.body })
      : isMutation
        ? readOnlyBlocked(method, url.pathname)
        : unhandled();
    const request = { method: method.toUpperCase(), url: url.toString(), transport: "fetch" };
    if (!result.handled) {
      audit("blocked", request);
      console.warn("[Pilcore Demo] Blocked network request", request);
      return new Response(
        JSON.stringify(result.data || { error: "Blocked by Pilcore demo mode", demo_mode: true }),
        { status: result.status || 501, headers: { "content-type": "application/json", "x-pilcore-demo": "1" } }
      );
    }
    audit("handled", { ...request, status: result.status });
    return new Response(JSON.stringify(result.data), {
      status: result.status,
      headers: { "content-type": "application/json", "x-pilcore-demo": "1" },
    });
  };

  Object.defineProperty(guardedFetch, "__pilcoreDemoGuard", { value: true });
  window.fetch = guardedFetch;
}
